import { useCallback, useEffect, useState } from 'react';

const UNMEASURED = { width: 0, height: 0 };

const snapToStep = (value, step) => Math.round(value / step) * step;

/**
 * Measured size of an element in css pixels, kept up to date as it resizes.
 *
 * Takes the element through a callback ref rather than a ref object: an effect keyed on a
 * ref object runs only once, so an element rendered after the first commit, or swapped
 * for another, would never be measured and DisplayMap would never show a map.
 *
 * Measured once when the element is attached so that callers are not left with a zero
 * size for a frame, then tracked with a ResizeObserver, falling back to window resize
 * events where that is unavailable. DisplayMap will not render a map at all until this
 * reports a size, so the fallback matters: without it the map would never appear.
 *
 * Sizes are snapped to `step` so that dragging a window edge does not emit a stream of
 * slightly different values.
 *
 * @param {number} step Granularity of the reported size, in css pixels.
 * @returns {{ ref: (element: HTMLElement | null) => void, width: number, height: number }}
 *   Pass `ref` to the element. Width and height are zero until it has been measured.
 */
const useElementSize = (step = 1) => {
  const [element, setElement] = useState(null);
  const [size, setSize] = useState(UNMEASURED);

  const ref = useCallback((nextElement) => {
    setElement(nextElement);
    setSize(UNMEASURED);
  }, []);

  useEffect(() => {
    if (!element) {
      return undefined;
    }

    const measure = () => {
      const { width, height } = element.getBoundingClientRect();

      setSize((currentSize) => {
        const nextSize = { width: snapToStep(width, step), height: snapToStep(height, step) };
        const isUnchanged =
          nextSize.width === currentSize.width && nextSize.height === currentSize.height;

        return isUnchanged ? currentSize : nextSize;
      });
    };

    measure();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);

      return () => window.removeEventListener('resize', measure);
    }

    const observer = new ResizeObserver(measure);
    observer.observe(element);

    return () => observer.disconnect();
  }, [element, step]);

  return { ref, ...size };
};

export default useElementSize;
